import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getRandomCodingChallenge, submitCodingSolution } from '../api/coding';

const MONACO_LANG = {
  javascript: 'javascript',
  python: 'python',
  java: 'java',
  cpp: 'cpp',
};

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function descriptionHtml(text) {
  const escaped = escapeHtml(text || '');
  return escaped.replace(/\n/g, '<br />');
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-white text-slate-900 py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">{children}</div>
    </div>
  );
}

export default function CodingInterviewPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [payload, setPayload] = useState(null);
  const [code, setCode] = useState('');
  const [editorLang, setEditorLang] = useState('javascript');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const submittedRef = useRef(false);

  const challenge = payload?.challenge;

  useEffect(() => {
    if (!token) {
      setError('Invalid link — missing access token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getRandomCodingChallenge(sessionId, token)
      .then((res) => {
        if (cancelled) return;
        setPayload(res.data);
        const c = res.data?.challenge;
        const lang = c?.language || 'javascript';
        setEditorLang(MONACO_LANG[lang] || lang);
        setCode(c?.starterCode || '');
        setSecondsLeft((res.data?.durationMinutes ?? 30) * 60);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 410) {
          setError('This coding round link has expired.');
        } else {
          setError('We could not load your coding challenge. The link may be invalid.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId, token]);

  const doSubmit = useCallback(async () => {
    if (submittedRef.current || !token) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await submitCodingSolution(sessionId, {
        code,
        language: editorLang,
        token,
      });
      setSubmitted(true);
    } catch (err) {
      submittedRef.current = false;
      setError(err.response?.data?.message || err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, token, code, editorLang]);

  useEffect(() => {
    if (secondsLeft == null || submitted || error) return;
    if (secondsLeft <= 0) {
      doSubmit();
      return;
    }
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [secondsLeft, submitted, error, doSubmit]);

  const timerLabel = useMemo(() => {
    if (secondsLeft == null) return '—';
    const m = Math.floor(secondsLeft / 60);
    const s = secondsLeft % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [secondsLeft]);

  if (loading) {
    return (
      <PageShell>
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </PageShell>
    );
  }

  if (error && !challenge) {
    return (
      <PageShell>
        <div className="rounded-xl border border-slate-200 p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Coding round unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <h1 className="text-xl font-bold text-emerald-900 mb-2">Solution submitted!</h1>
          <p className="text-sm text-emerald-800">
            Results will be reviewed by the hiring team. You may close this page.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-lg font-bold text-[#1B2B4B]">Samastix</p>
          {payload?.orgName && <p className="text-sm text-slate-500">{payload.orgName}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Time remaining</p>
          <p className={`text-2xl font-mono font-bold tabular-nums ${secondsLeft != null && secondsLeft < 300 ? 'text-red-600' : 'text-slate-800'}`}>
            {timerLabel}
          </p>
        </div>
      </header>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 sm:p-6 mb-4 shadow-sm">
        <h1 className="text-xl font-bold text-slate-900">{challenge?.title}</h1>
        <div className="flex flex-wrap gap-2 mt-2 mb-4">
          <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-slate-200 text-slate-700 capitalize">
            {challenge?.language}
          </span>
          <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200/60 capitalize">
            {challenge?.difficulty}
          </span>
        </div>
        <div
          className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: descriptionHtml(challenge?.description) }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <label className="text-xs font-semibold text-slate-500 uppercase">Editor language</label>
        <select
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
          value={editorLang}
          onChange={(e) => setEditorLang(e.target.value)}
        >
          <option value="javascript">JavaScript</option>
          <option value="python">Python</option>
          <option value="java">Java</option>
          <option value="cpp">C++</option>
        </select>
      </div>

      <div className="rounded-lg border border-slate-300 overflow-hidden mb-4" style={{ height: '60vh' }}>
        <Editor
          height="100%"
          language={editorLang}
          value={code}
          onChange={(v) => setCode(v ?? '')}
          theme="vs"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
          }}
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">{error}</p>
      )}

      <button
        type="button"
        disabled={submitting}
        onClick={doSubmit}
        className="btn btn-primary w-full sm:w-auto px-6 py-3"
      >
        {submitting ? 'Submitting…' : 'Submit Solution'}
      </button>

      <footer className="mt-10 text-center text-xs text-slate-400">
        Powered by Samastix · AI-verified interviews
      </footer>
    </PageShell>
  );
}
